<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import {
    ColoredButton,
    FieldSearchBar,
    SearchToolbar,
    SelectInput,
    PaginateOrScroll,
  } from '@/shared/ui';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import {
    clearBreadcrumbTrail,
    formatInstantDateToPtBR,
    setBreadcrumbTrail,
    useSearchUrlSync,
  } from '@/shared/lib';
  import type { EntityType } from '@/shared/lib';
  import { QUANTITY_OPTIONS, SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
  import ModelsTable from './ModelsTable.vue';
  import { buildQuantityOptions } from '../lib/quantity-options';
  import { useRecordSearch } from '../model/useRecordSearch';
  import { useModelListControls } from '../model/useModelListControls';
  import { useRejectedListControls } from '../model/useRejectedListControls';
  import { useDraftListControls } from '../model/useDraftListControls';
  import { useActiveListControls } from '../model/useActiveListControls';
  import { useDraftActions } from '../model/useDraftActions';
  import { useRejectedActions } from '../model/useRejectedActions';
  import { useModelActions } from '../model/useModelActions';
  import { useModelPermissions } from '../model/useModelPermissions';
  import { useRecordSelection } from '../model/useRecordSelection';
  import { listMatrixModels } from '../api/matrix-models.api';
  import { countRejectedMatrices, listMatrices, listRejectedMatrices } from '../api/matrices.api';
  import type { ModelItem, DraftItem, RejectedItem, RecordItem } from '../model/models.types';
  import type { RecordFetcher } from '../model/useRecordSearch';
  import type { MatrixModelSearchType } from '../api/matrix-models.api';
  import type { MatrixSearchType } from '../api/matrices.api';

  const route = useRoute();
  const router = useRouter();

  const SEARCH_FIELDS_BY_TAB: Record<string, { id: string; name: string }[]> = {
    modelos: [
      { id: 'title', name: 'Título' },
      { id: 'category', name: 'Categoria' },
    ],
    rascunhos: [
      { id: 'title', name: 'Título' },
      { id: 'registro', name: 'Registro' },
    ],
    reprovados: [
      { id: 'registro', name: 'Registro' },
      { id: 'title', name: 'Título' },
    ],
  };

  const DEFAULT_SEARCH_FIELD_BY_TAB: Record<string, string> = {
    reprovados: 'registro',
  };

  const defaultSearchField = (tab: string) => DEFAULT_SEARCH_FIELD_BY_TAB[tab] ?? 'title';

  const ENTITY_BY_TAB: Record<string, EntityType> = {
    modelos: 'modelo',
    rascunhos: 'rascunho',
    reprovados: 'registro',
  };

  const LOADING_COLUMN_SIZE_BY_TAB: Record<string, number> = {
    modelos: 4,
    rascunhos: 5,
    reprovados: 6,
  };

  const ERROR_TITLE_BY_TAB: Record<string, string> = {
    reprovados: 'Erro ao carregar registros reprovados',
  };

  const rejectedCount = ref<number | null>(null);

  const subTabs = computed(() => [
    { id: 'modelos', text: 'Modelos' },
    { id: 'rascunhos', text: 'Rascunhos' },
    {
      id: 'reprovados',
      text: 'Reprovados',
      badge:
        rejectedCount.value === null ? undefined : String(rejectedCount.value).padStart(2, '0'),
    },
  ]);

  const MODELS_BREADCRUMB_TRAIL = [
    { name: 'Gestão de Registros', path: '/gestao-registros' },
    { name: 'Modelos', path: '/gestao-registros/modelos' },
  ];

  const refreshRejectedCount = async () => {
    try {
      rejectedCount.value = await countRejectedMatrices();
    } catch (error) {
      console.error('Erro ao contar registros reprovados:', error);
    }
  };

  const tabFromQuery = route.query.tab as string | undefined;
  const initialTab = tabFromQuery && SEARCH_FIELDS_BY_TAB[tabFromQuery] ? tabFromQuery : 'modelos';
  const initialPage = Number(route.query.page) > 1 ? Number(route.query.page) : 1;

  const activeSubTab = ref(initialTab);
  const {
    searchTerm: term,
    searchFieldId,
    syncSearchUrl,
  } = useSearchUrlSync({
    fields: SEARCH_FIELDS_BY_TAB[initialTab].map((f) => f.id),
    defaultField: defaultSearchField(initialTab),
    fieldQueryKey: 'searchType',
  });

  watch(
    activeSubTab,
    (tab) => {
      const subTabName = subTabs.value.find((subTab) => subTab.id === tab)?.text ?? 'Modelos';
      setBreadcrumbTrail([...MODELS_BREADCRUMB_TRAIL, { name: subTabName, path: route.path }]);
    },
    { immediate: true }
  );

  const isModelsTab = computed(() => activeSubTab.value === 'modelos');

  const isSearchActive = computed(() => term.value.trim() !== '');
  const entityName = computed<EntityType>(() => ENTITY_BY_TAB[activeSubTab.value] ?? 'modelo');
  const searchFields = computed(() => SEARCH_FIELDS_BY_TAB[activeSubTab.value]);

  const fetchModels: RecordFetcher<ModelItem> = async ({ page, itemsPerPage }) => {
    const { data, metadata } = await listMatrixModels({
      page,
      itemsPerPage,
      term: term.value || undefined,
      searchType: searchFieldId.value as MatrixModelSearchType,
      ...controls.requestParams.value,
    });
    return {
      data: data.map((model) => ({
        id: model.id,
        title: model.title,
        categories: model.categories,
      })),
      metadata,
    };
  };

  const fetchDrafts: RecordFetcher<DraftItem> = async ({ page, itemsPerPage }) => {
    const { data, metadata } = await listMatrices({
      page,
      itemsPerPage,
      term: term.value || undefined,
      searchType: searchFieldId.value as MatrixSearchType,
      ...draftControls.requestParams.value,
    });
    return {
      data: data.map((matrix) => ({
        id: matrix.id,
        registry: matrix.prefix,
        title: matrix.title,
        savedDate: formatInstantDateToPtBR(matrix.updatedAt ?? matrix.createdAt),
      })),
      metadata,
    };
  };

  const fetchRejected: RecordFetcher<RejectedItem> = async ({ page, itemsPerPage }) => {
    void refreshRejectedCount();
    const { data, metadata } = await listRejectedMatrices({
      page,
      itemsPerPage,
      term: term.value || undefined,
      searchType: searchFieldId.value as MatrixSearchType,
      ...rejectedControls.requestParams.value,
    });
    return {
      data: data.map((matrix) => ({
        id: matrix.id,
        registry: matrix.code,
        title: matrix.title,
        revision: matrix.revision === null ? '-' : String(matrix.revision).padStart(2, '0'),
        rejectionDate: formatInstantDateToPtBR(matrix.rejectedAt),
      })),
      metadata,
    };
  };

  const fetcher = computed<RecordFetcher<RecordItem>>(() => {
    if (activeSubTab.value === 'rascunhos') return fetchDrafts as RecordFetcher<RecordItem>;
    if (activeSubTab.value === 'reprovados') return fetchRejected as RecordFetcher<RecordItem>;
    return fetchModels as RecordFetcher<RecordItem>;
  });

  const {
    clear,
    items,
    allItems,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    selectedQuantity,
    currentPage,
    isShowAllMode,
    refetch,
    reload,
    patchItem,
    onPageChanged,
    handleQuantityChange,
  } = useRecordSearch<RecordItem>(
    fetcher,
    () => ERROR_TITLE_BY_TAB[activeSubTab.value] ?? 'Erro ao carregar registros'
  );

  const shouldHideSummary = computed(() => {
    if (loading.value) return true;
    const visibleItems = isShowAllMode.value ? allItems.value : items.value;
    return visibleItems.length === 0;
  });

  // Declared after `useRecordSearch` because it drives the refetch, and reached by `fetchModels`
  // only when a request runs, which is always after setup.
  const controls = useModelListControls({
    refetch: () => refetch(),
    onCommitted: () => syncUrl(),
  });

  const draftControls = useDraftListControls({
    refetch: () => refetch(),
    onCommitted: () => syncUrl(),
  });

  const rejectedControls = useRejectedListControls({
    refetch: () => refetch(),
    onCommitted: () => syncUrl(),
  });

  const listControls = useActiveListControls({
    activeSubTab,
    controlsByTab: { modelos: controls, rascunhos: draftControls, reprovados: rejectedControls },
  });

  const visibleRows = computed(() => (isShowAllMode.value ? allItems.value : items.value));

  /**
   * Any change to what the listing shows invalidates a selection of ids, so the reset key covers
   * the whole query: ids the user can no longer see must never reach the bulk deletion. The page
   * stays out of the key in "show all" mode, where a new batch appends rows and hides none.
   */
  const listingKey = computed(() =>
    [
      activeSubTab.value,
      isShowAllMode.value ? '' : currentPage.value,
      selectedQuantity.value,
      term.value.trim(),
      searchFieldId.value,
      JSON.stringify(listControls.urlQuery.value),
    ].join('|')
  );

  const selection = useRecordSelection(
    () => visibleRows.value.length,
    () => listingKey.value
  );

  const { canRename, canDelete } = useModelPermissions();

  const refreshListing = async () => {
    await reload();
    syncUrl();
    const listedIds = new Set(visibleRows.value.map((row) => row.id));
    [...selection.selectedIds.value]
      .filter((id) => !listedIds.has(id))
      .forEach((id) => selection.remove(id));
  };
  const selectedIds = () => [...selection.selectedIds.value];
  const removeFromSelection = (ids: number[]) => ids.forEach((id) => selection.remove(id));

  const actions = useModelActions({
    refresh: refreshListing,
    patchTitle: (id, title) => patchItem(id, { title }),
    selectedIds,
    removeFromSelection,
  });

  const draftActions = useDraftActions({
    patchRenamed: (id, title, updatedAt) =>
      patchItem(id, { title, savedDate: formatInstantDateToPtBR(updatedAt) }),
    refresh: refreshListing,
    selectedIds,
    removeFromSelection,
  });

  const rejectedActions = useRejectedActions({
    refresh: refreshListing,
    selectedIds,
    removeFromSelection,
  });

  const handleBulkDelete = () => {
    if (isModelsTab.value) return actions.openBulkDeleteConfirmation();
    if (activeSubTab.value === 'rascunhos') return draftActions.openBulkDeleteConfirmation();
    if (activeSubTab.value === 'reprovados') return rejectedActions.openBulkDeleteConfirmation();
  };

  const loadingColumnSize = computed(() => LOADING_COLUMN_SIZE_BY_TAB[activeSubTab.value] ?? 4);

  const quantityOptions = computed(() =>
    buildQuantityOptions(QUANTITY_OPTIONS, SHOW_ALL_ITEMS_QUANTITY, activeSubTab.value)
  );

  const syncUrl = () =>
    syncSearchUrl({
      tab: activeSubTab.value,
      ...(currentPage.value > 1 ? { page: currentPage.value } : {}),
      ...listControls.urlQuery.value,
    });

  const createMatrix = () => {
    router.push('/construtor-formularios');
  };

  const handleSearch = async (searchTerm: string) => {
    term.value = searchTerm;
    await refetch();
    syncUrl();
  };

  const handleQuantitySelected = async (value: string | number) => {
    await handleQuantityChange(value);
    syncUrl();
  };

  const handlePageChanged = async (page: number) => {
    await onPageChanged(page);
    syncUrl();
  };

  const handleSubTabChange = async (id: string) => {
    if (id === activeSubTab.value) return;
    activeSubTab.value = id;
    searchFieldId.value = defaultSearchField(id);
    term.value = '';
    listControls.resetAll();
    clear();
    await refetch();
    syncUrl();
  };

  onMounted(async () => {
    if (initialTab !== 'reprovados') void refreshRejectedCount();
    if (initialPage > 1) {
      await onPageChanged(initialPage);
    } else {
      await refetch();
    }
  });

  onBeforeUnmount(clearBreadcrumbTrail);
</script>

<template>
  <div class="flex flex-col gap-6">
    <ColoredButton
      text="Criar Matriz"
      :icon="PlusIcon"
      icon-alt="Criar Matriz"
      class="w-fit"
      @click="createMatrix"
    />

    <SearchToolbar>
      <template #search>
        <FieldSearchBar
          :key="activeSubTab"
          v-model:search-field-id="searchFieldId"
          :search-fields="searchFields"
          :initial-search-term="term"
          independent-search-field
          @search="handleSearch"
        />
      </template>
      <template #quantity>
        <SelectInput
          :model-value="selectedQuantity"
          :options="quantityOptions"
          class="cursor-pointer"
          @update:model-value="handleQuantitySelected"
        />
      </template>
    </SearchToolbar>

    <PaginateOrScroll
      :items="items"
      :all-items="allItems"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      :loading-more="loadingMore"
      :has-more-items="hasMoreItems"
      :entity-name="entityName"
      :hide-summary="shouldHideSummary"
      @page-changed="handlePageChanged"
    >
      <template #table="{ items: visibleItems }">
        <ModelsTable
          :records="visibleItems as RecordItem[]"
          :sub-tab-options="subTabs"
          :active-sub-tab="activeSubTab"
          :order-options="listControls.orderOptions.value"
          :current-sort="listControls.currentSort.value"
          :current-order="listControls.currentOrder.value"
          :is-search-active="isSearchActive"
          :is-filter-active="listControls.isFilterActive.value"
          :loading="loading"
          :loading-column-size="loadingColumnSize"
          :selected-ids="selection.selectedIds.value"
          :can-rename="canRename"
          :can-delete="canDelete"
          @tab-change="handleSubTabChange"
          @update:sort="listControls.handleSortUpdate"
          @filter-clicked="listControls.openFilterModal"
          @toggle-selection="selection.toggle"
          @toggle-all="selection.selectAll"
          @bulk-delete="handleBulkDelete"
          @rename="actions.openRename"
          @rename-draft="draftActions.openRename"
          @delete="actions.openSingleDeleteConfirmation"
          @delete-draft="draftActions.openSingleDeleteConfirmation"
          @delete-rejected="rejectedActions.openSingleDeleteConfirmation"
        />
      </template>
    </PaginateOrScroll>
  </div>
</template>
